// See https://claude.ai/chat/b49102bf-6b93-4dea-8556-54ce03742e44
// NOTE(liamvdv): Add a interface here ending in 'Event' and add the event type to the 'Event' type union.
// Example usage:
/*
const { publish, subscribe } = usePubSub();

// Type-safe subscription
subscribe("message_end", (data) => {
  // data is typed as { conversation_id: string; message_id: string }
  console.log(data.conversation_id);
});

// Type-safe emission
publish({
  type: "message_end",
  conversation_id: "3be061fe-8c91-4313-9646-b0d673ac7787",
  message_id: "32505a8e-d8df-44f3-8ff8-ec35d68eb3ea"
});
*/
import { useEffect, useCallback, useRef } from "react";
import { useAppSelector } from "./store";

/*********************************** Edit *************************************/

export interface MessageEndEvent {
  type: "message_end";
  conversation_id: string;
  message_id: string;
}

export interface MessageStartEvent {
  type: "message_start";
  conversation_id: string | undefined;
}

export interface MessageRatingEvent {
  type: "message_rating";
  conversation_id: string;
  message_id: string;
  rating: string;
}

type Event = MessageStartEvent | MessageEndEvent | MessageRatingEvent;

/******************************** Leave alone *********************************/

// Get all possible event types
type EventTypes = Event["type"];

// Helper type to get event type based on the type key
type EventFromType<T extends EventTypes> = Extract<Event, { type: T }>;

// Changed: EventCallback now receives the full event type
type EventCallback<T extends EventTypes> = (data: EventFromType<T>) => void;

type Subscription = {
  type: EventTypes;
  // We store the callback in a more generic way but it's type-safe when used
  callback: (data: any) => void;
};

/**
 * Custom hook only for cross-tab/browser-instance pub/sub communication.
 * Events are not seen by the sender tab.
 * @returns {Object} Methods for publishing and subscribing to events
 */
export const usePubSub = (channelName: string) => {
  // Use ref to maintain single channel instance
  const channelRef = useRef<BroadcastChannel | null>(null);
  // Store subscriptions in a ref to avoid recreating listeners
  const subscriptionsRef = useRef<Subscription[]>([]);

  useEffect(() => {
    // Create channel instance if it doesn't exist
    if (!channelRef.current) {
      channelRef.current = new BroadcastChannel(channelName);
    }

    const channel = channelRef.current;

    // Message handler that routes events to appropriate subscribers
    const handleMessage = (event: MessageEvent) => {
      const eventData = event.data as Event;
      subscriptionsRef.current
        .filter((sub) => sub.type === eventData.type)
        .forEach((sub) => {
          sub.callback(eventData);
        });
    };

    channel.addEventListener("message", handleMessage);

    // Cleanup
    return () => {
      channel.removeEventListener("message", handleMessage);
      channel.close();
      channelRef.current = null;
      subscriptionsRef.current = [];
    };
  }, [channelName]);

  // Emit an event to all subscribers including other tabs
  const publish = useCallback((event: Event): void => {
    if (!channelRef.current) return;
    channelRef.current.postMessage(event);
  }, []);

  // Subscribe to events of a specific type
  const subscribe = useCallback(
    <T extends EventTypes>(
      type: T,
      callback: EventCallback<T>
    ): (() => void) => {
      const subscription: Subscription = {
        type,
        callback: (data: any) => callback(data as EventFromType<T>),
      };
      subscriptionsRef.current.push(subscription);

      // Return unsubscribe function
      return () => {
        subscriptionsRef.current = subscriptionsRef.current.filter(
          (sub) => sub !== subscription
        );
      };
    },
    []
  );

  return { publish, subscribe };
};

export const useFramePubSub = () => {
  const frameId = useAppSelector((state) => state.config.data.frameId);
  const channelName = `botbrains_events_${frameId}`;
  return usePubSub(channelName);
};
